import type { ThunkAction, UnknownAction } from "@reduxjs/toolkit";

import {
	AddPortalCustomDomainMutation,
	ChangePortalAvailabilityRuleMutation,
	ChangePortalCheckoutMethodMutation,
	ChangePortalHelpContactMutation,
	PortalAddContactMutation,
	PortalAddLineItemsMutation,
	PortalCompleteCheckoutMutation,
	PortalCreateCartMutation,
	PortalCreateFavoritesListMutation,
	PortalDeleteEmptyOrderMutation,
	PortalDeleteFavoritesListMutation,
	PortalGetNextOrderNumberMutation,
	PortalInitializeOrderMutation,
	PortalPlaceOrderMutation,
	PortalRemoveContactMutation,
	PortalSetFavoritesListActiveMutation,
	PortalUpdateCartMutation,
	PortalUpdateContactMutation,
	PortalUpdateFavoritesListMutation,
	RemovePortalCustomDomainMutation,
	SetPrimaryPortalCustomDomainMutation,
	UpdatePortalSettingsMutation,
	VerifyPortalCustomDomainMutation
} from "~/gql/mutations/portal.generated";
import {
	CustomerPortalCreatePortalUserMutation,
	CustomerPortalDeactivatePortalUserMutation,
	CustomerPortalUpdatePortalUserMutation
} from "~/gql/mutations/portalUsers.generated";
import {
	GetCustomerPortalEligibleDeliveryDatesQuery,
	GetCustomerPortalSellableHardgoodsQuery,
	GetCustomerPortalSettingsQuery,
	GetPortalAccountQuery,
	GetPortalCartQuery,
	GetPortalCustomerOrderLinesQuery,
	GetPortalCustomerOrdersQuery,
	GetPortalFavoritesListQuery,
	GetPortalFavoritesListsQuery,
	GetPortalItemAvailabilityQuery,
	GetPortalItemPriceQuery,
	GetPortalItemPriceQueryVariables,
	GetPortalItemPricesQuery,
	GetPortalItemsByIdsQuery,
	GetPortalItemsQuery,
	GetPortalPriceGroupQuery,
	GetPortalPriceLevelsQuery,
	GetPortalSettingsQuery
} from "~/gql/queries/portal.generated";
import applyConfirmedPortalSettings from "~/helpers/applyConfirmedPortalSettings";

const importApiEndpointsLazilyToBreakCycle = async () =>
	(await import("~/services/endpoints/apiEndpoints")).apiEndpoints;

type CycleFreeLifecycleDispatch = <ReturnValue, State>(
	thunkAction: ThunkAction<ReturnValue, State, unknown, UnknownAction>
) => ReturnValue;

interface UpdatePortalSettingsLifecycle {
	dispatch: CycleFreeLifecycleDispatch;
	queryFulfilled: Promise<{ data: UpdatePortalSettingsMutation }>;
}

interface ChangePortalCheckoutMethodLifecycle {
	dispatch: CycleFreeLifecycleDispatch;
	queryFulfilled: Promise<{ data: ChangePortalCheckoutMethodMutation }>;
}

interface ChangePortalHelpContactLifecycle {
	dispatch: CycleFreeLifecycleDispatch;
	queryFulfilled: Promise<{ data: ChangePortalHelpContactMutation }>;
}

export const portalEndpoints = {
	// Mutations
	AddPortalCustomDomain: {
		invalidatesTags: ["PortalSettings"] as AddTagTypes,
		transformResponse: (res: AddPortalCustomDomainMutation) => res
	},
	ChangePortalAvailabilityRule: {
		invalidatesTags: ["PortalSettings"] as AddTagTypes,
		transformResponse: (res: ChangePortalAvailabilityRuleMutation) => res
	},
	ChangePortalCheckoutMethod: {
		invalidatesTags: ["PortalSettings"] as AddTagTypes,
		async onQueryStarted(_arg: unknown, { dispatch, queryFulfilled }: ChangePortalCheckoutMethodLifecycle) {
			const apiEndpoints = await importApiEndpointsLazilyToBreakCycle();
			try {
				const { data } = await queryFulfilled;
				dispatch(
					apiEndpoints.util.updateQueryData("GetPortalSettings", undefined, (draft) =>
						applyConfirmedPortalSettings(data.changePortalCheckoutMethod, draft)
					)
				);
			} catch {}
		},
		transformResponse: (res: ChangePortalCheckoutMethodMutation) => res
	},
	ChangePortalHelpContact: {
		invalidatesTags: ["PortalSettings"] as AddTagTypes,
		async onQueryStarted(_arg: unknown, { dispatch, queryFulfilled }: ChangePortalHelpContactLifecycle) {
			const apiEndpoints = await importApiEndpointsLazilyToBreakCycle();
			try {
				const { data } = await queryFulfilled;
				dispatch(
					apiEndpoints.util.updateQueryData("GetPortalSettings", undefined, (draft) =>
						applyConfirmedPortalSettings(data.changePortalHelpContact, draft)
					)
				);
			} catch {}
		},
		transformResponse: (res: ChangePortalHelpContactMutation) => res
	},
	CustomerPortalCreatePortalUser: {
		invalidatesTags: ["PortalUsers"] as AddTagTypes,
		transformResponse: (res: CustomerPortalCreatePortalUserMutation) => res
	},
	CustomerPortalDeactivatePortalUser: {
		invalidatesTags: ["PortalUsers"] as AddTagTypes,
		transformResponse: (res: CustomerPortalDeactivatePortalUserMutation) => res
	},
	CustomerPortalUpdatePortalUser: {
		invalidatesTags: ["PortalUsers"] as AddTagTypes,
		transformResponse: (res: CustomerPortalUpdatePortalUserMutation) => res
	},
	// Queries
	GetCustomerPortalEligibleDeliveryDates: {
		providesTags: ["PortalDeliveryDates"] as AddTagTypes,
		transformResponse: (res: GetCustomerPortalEligibleDeliveryDatesQuery) => res
	},
	GetCustomerPortalSellableHardgoods: {
		providesTags: ["PortalCatalog"] as AddTagTypes,
		transformResponse: (res: GetCustomerPortalSellableHardgoodsQuery) => res
	},
	GetCustomerPortalSettings: {
		providesTags: ["PortalSettings"] as AddTagTypes,
		transformResponse: (res: GetCustomerPortalSettingsQuery) => res
	},
	GetPortalAccount: {
		providesTags: ["PortalAccount"] as AddTagTypes,
		transformResponse: (res: GetPortalAccountQuery) => res
	},
	GetPortalCart: {
		providesTags: ["PortalCart"] as AddTagTypes,
		transformResponse: (res: GetPortalCartQuery) => res
	},
	GetPortalCustomerOrderLines: {
		providesTags: ["PortalCustomerOrderLines"] as AddTagTypes,
		transformResponse: (res: GetPortalCustomerOrderLinesQuery) => res
	},
	GetPortalCustomerOrders: {
		providesTags: ["PortalCustomerOrders"] as AddTagTypes,
		transformResponse: (res: GetPortalCustomerOrdersQuery) => res
	},
	GetPortalFavoritesList: {
		providesTags: ["PortalFavoritesList"] as AddTagTypes,
		transformResponse: (res: GetPortalFavoritesListQuery) => res
	},
	GetPortalFavoritesLists: {
		providesTags: ["PortalFavoritesList"] as AddTagTypes,
		transformResponse: (res: GetPortalFavoritesListsQuery) => res
	},
	GetPortalItemAvailability: {
		providesTags: ["PortalCatalog"] as AddTagTypes,
		transformResponse: (res: GetPortalItemAvailabilityQuery) => res
	},
	GetPortalItemPrice: {
		providesTags: (_res: GetPortalItemPriceQuery | undefined, _err: unknown, arg: GetPortalItemPriceQueryVariables) => [
			{ id: arg.itemId, type: "PortalPrice" as const }
		],
		transformResponse: (res: GetPortalItemPriceQuery) => res
	},

	GetPortalItemPrices: {
		providesTags: (res: GetPortalItemPricesQuery | undefined) => [
			{ id: "LIST", type: "PortalPrice" as const },
			...(res?.itemPrices.itemPrices ?? []).map((itemPrice) => ({ id: itemPrice.itemId, type: "PortalPrice" as const }))
		],
		transformResponse: (res: GetPortalItemPricesQuery) => res
	},
	GetPortalItems: {
		providesTags: ["PortalCatalog"] as AddTagTypes,
		transformResponse: (res: GetPortalItemsQuery) => res
	},
	GetPortalItemsByIds: {
		providesTags: ["PortalCatalog"] as AddTagTypes,
		transformResponse: (res: GetPortalItemsByIdsQuery) => res
	},
	GetPortalPriceGroup: {
		transformResponse: (res: GetPortalPriceGroupQuery) => res
	},
	GetPortalPriceLevels: {
		providesTags: (res: GetPortalPriceLevelsQuery | undefined) => [
			{ id: "LIST", type: "PortalPrice" as const },
			...(res?.priceLevels?.nodes ?? []).map((priceLevel) => ({ id: priceLevel.id, type: "PortalPrice" as const }))
		],
		transformResponse: (res: GetPortalPriceLevelsQuery) => res
	},
	GetPortalSettings: {
		providesTags: ["PortalSettings"] as AddTagTypes,
		transformResponse: (res: GetPortalSettingsQuery) => res
	},
	PortalAddContact: {
		invalidatesTags: ["PortalAccount"] as AddTagTypes,
		transformResponse: (res: PortalAddContactMutation) => res
	},
	PortalAddLineItems: {
		invalidatesTags: ["PortalOrders"] as AddTagTypes,
		transformResponse: (res: PortalAddLineItemsMutation) => res
	},
	PortalCompleteCheckout: {
		invalidatesTags: ["PortalOrders"] as AddTagTypes,
		transformResponse: (res: PortalCompleteCheckoutMutation) => res
	},
	PortalCreateCart: {
		invalidatesTags: ["PortalCart"] as AddTagTypes,
		transformResponse: (res: PortalCreateCartMutation) => res
	},
	PortalCreateFavoritesList: {
		invalidatesTags: ["PortalFavoritesList"] as AddTagTypes,
		transformResponse: (res: PortalCreateFavoritesListMutation) => res
	},
	PortalDeleteEmptyOrder: {
		invalidatesTags: ["PortalOrders"] as AddTagTypes,
		transformResponse: (res: PortalDeleteEmptyOrderMutation) => res
	},
	PortalDeleteFavoritesList: {
		invalidatesTags: ["PortalFavoritesList"] as AddTagTypes,
		transformResponse: (res: PortalDeleteFavoritesListMutation) => res
	},
	PortalGetNextOrderNumber: {
		transformResponse: (res: PortalGetNextOrderNumberMutation) => res
	},
	PortalPlaceOrder: {
		invalidatesTags: ["PortalOrders", "PortalDeliveryDates"] as AddTagTypes,
		transformResponse: (res: PortalPlaceOrderMutation) => res
	},
	PortalInitializeOrder: {
		invalidatesTags: ["PortalOrders", "PortalDeliveryDates"] as AddTagTypes,
		transformResponse: (res: PortalInitializeOrderMutation) => res
	},
	PortalRemoveContact: {
		invalidatesTags: ["PortalAccount"] as AddTagTypes,
		transformResponse: (res: PortalRemoveContactMutation) => res
	},
	PortalSetFavoritesListActive: {
		invalidatesTags: ["PortalFavoritesList"] as AddTagTypes,
		transformResponse: (res: PortalSetFavoritesListActiveMutation) => res
	},
	PortalUpdateCart: {
		invalidatesTags: ["PortalCart"] as AddTagTypes,
		transformResponse: (res: PortalUpdateCartMutation) => res
	},
	PortalUpdateContact: {
		invalidatesTags: ["PortalAccount"] as AddTagTypes,
		transformResponse: (res: PortalUpdateContactMutation) => res
	},
	PortalUpdateFavoritesList: {
		invalidatesTags: ["PortalFavoritesList"] as AddTagTypes,
		transformResponse: (res: PortalUpdateFavoritesListMutation) => res
	},
	RemovePortalCustomDomain: {
		invalidatesTags: ["PortalSettings"] as AddTagTypes,
		transformResponse: (res: RemovePortalCustomDomainMutation) => res
	},
	SetPrimaryPortalCustomDomain: {
		invalidatesTags: ["PortalSettings"] as AddTagTypes,
		transformResponse: (res: SetPrimaryPortalCustomDomainMutation) => res
	},
	UpdatePortalSettings: {
		invalidatesTags: ["PortalSettings"] as AddTagTypes,
		async onQueryStarted(_arg: unknown, { dispatch, queryFulfilled }: UpdatePortalSettingsLifecycle) {
			const apiEndpoints = await importApiEndpointsLazilyToBreakCycle();
			try {
				const { data } = await queryFulfilled;
				dispatch(
					apiEndpoints.util.updateQueryData("GetPortalSettings", undefined, (draft) =>
						applyConfirmedPortalSettings(data.updatePortalSettings, draft)
					)
				);
			} catch {}
		},
		transformResponse: (res: UpdatePortalSettingsMutation) => res
	},
	VerifyPortalCustomDomain: {
		invalidatesTags: ["PortalSettings"] as AddTagTypes,
		transformResponse: (res: VerifyPortalCustomDomainMutation) => res
	}
};
